/**
 * Service for managing shake detection settings and preferences
 */

const STORAGE_KEY = 'shake_detection_enabled';
const SHAKE_COOLDOWN_MS = 2000; // 2 second cooldown between shakes

// Global timestamp of last shake (not persisted, resets on page reload)
let lastGlobalShakeTime = 0;

export const shakeDetectionService = {
  /**
   * Check if shake detection is enabled in localStorage
   * @returns {boolean}
   */
  isEnabled: () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'true';
    } catch (error) {
      console.error('Error reading shake detection setting:', error);
      return false;
    }
  },

  /**
   * Enable shake detection
   */
  enable: () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch (error) {
      console.error('Error saving shake detection setting:', error);
    }
  },

  /**
   * Disable shake detection
   */
  disable: () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'false');
    } catch (error) {
      console.error('Error saving shake detection setting:', error);
    }
  },

  /**
   * Toggle shake detection
   * @returns {boolean} - New state
   */
  toggle: () => {
    const current = shakeDetectionService.isEnabled();
    if (current) {
      shakeDetectionService.disable();
    } else {
      shakeDetectionService.enable();
    }
    return !current;
  },

  /**
   * Speak confirmation message using Speech Synthesis API
   * @param {string} message - Message to speak
   */
  speakConfirmation: (message) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(message);
      utterance.lang = 'de-DE';
      utterance.rate = 1.0;
      utterance.pitch = 1.0;

      window.speechSynthesis.speak(utterance);
    }
  },

  /**
   * Check if enough time has passed since last shake (cooldown check)
   * @returns {boolean} - True if shake can be triggered now
   */
  canShakeNow: () => {
    const now = Date.now();
    const timeSinceLastShake = now - lastGlobalShakeTime;
    const canShake = timeSinceLastShake >= SHAKE_COOLDOWN_MS;

    if (!canShake) {
      console.log('Shake cooldown active:', {
        timeSinceLastShake,
        cooldownMs: SHAKE_COOLDOWN_MS,
        remainingMs: SHAKE_COOLDOWN_MS - timeSinceLastShake
      });
    }

    return canShake;
  },

  /**
   * Update the global last shake timestamp
   * Call this after a shake is successfully triggered
   */
  setLastShakeTime: () => {
    lastGlobalShakeTime = Date.now();
    console.log('Global shake timestamp updated:', lastGlobalShakeTime);
  },
};
